import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';  
import { environment } from '../../environments/environment';
import { TipoEtapa, TipoIsencao } from '@/models';
import { PageResponse, StatusCount } from './pagination.types';

@Injectable({
  providedIn: 'root'
})
export class TipoIsencaoService {
  private apiUrl = `${environment.apiUrl}/admin/tipos-isencao`;

  constructor(private http: HttpClient) { }

  /**
   * Lista todos os tipos de isenções
   * @param apenasAtivos Se true, retorna apenas tipos ativos
   */
  getAll(apenasAtivos: boolean = true): Observable<TipoIsencao[]> {
    let params = new HttpParams();
    if (apenasAtivos) {
      params = params.set('apenasAtivos', apenasAtivos.toString());
    }

    return this.http.get<TipoIsencao[]>(this.apiUrl, { params });
  }

  /**
   * Busca tipo de isenção por ID
   * GET
   * /api/tipos-isencoes/{id}
   * Buscar tipo de isenção por ID
   * @param id ID do tipo de isenção
   * @returns Observable<TipoIsencao | undefined>
   */
  getById(id: number): Observable<TipoIsencao | undefined> {
    return this.http.get<TipoIsencao>(`${this.apiUrl}/${id}`);
  }

  /**
   * Cria um novo tipo de isenção
   * POST
   * /api/tipos-isencoes
   * Criar tipo de isenção
   * @param data Dados do tipo de isenção
   * @returns Observable<TipoIsencao>
   */
  create(data: TipoIsencao): Observable<TipoIsencao> {
    return this.http.post<TipoIsencao>(this.apiUrl, data);
  }

  /**
   * Atualiza um tipo de isenção existente
   * PUT
   * /api/tipos-isencoes/{id}
   * Atualizar tipo de isenção
   * @param id ID do tipo de isenção
   * @param data Dados do tipo de isenção
   * @returns Observable<TipoIsencao>
   */
  update(id: number, data: TipoIsencao): Observable<TipoIsencao> {
    return this.http.put<TipoIsencao>(`${this.apiUrl}/${id}`, data);
  }

  /**
   * Ativa um tipo de isenção existente
   * PATCH
   * /api/tipos-isencoes/{id}/toggle-status
   * Alternar status do tipo de isenção
   * @param id ID do tipo de isenção
   * @param active Status do tipo de isenção
   * @returns Observable<TipoIsencao>
   */
  toggleStatus(id: number, active: boolean): Observable<TipoIsencao> {
    return this.http.patch<TipoIsencao>(`${this.apiUrl}/${id}/toggle-status`, { active });
  }

  /**
   * Exclui um tipo de isenção existente
   * DELETE
   * /api/tipos-isencoes/{id}
   * Excluir tipo de isenção
   * @param id ID do tipo de isenção
   * @returns Observable<TipoIsencao>
   */
  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  /**
   * Lista tipos de isenção paginados
   * GET /api/admin/tipos-isencao/paginado
   */
  listarPaginado(
    page: number = 0,
    size: number = 10,
    sortBy: string = 'nome',
    sortDirection: 'ASC' | 'DESC' = 'ASC',
    apenasAtivos?: boolean
  ): Observable<PageResponse<TipoIsencao>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sortBy', sortBy)
      .set('sortDirection', sortDirection);
    
    if (apenasAtivos !== undefined) {
      params = params.set('apenasAtivos', apenasAtivos.toString());
    }
    
    return this.http.get<PageResponse<TipoIsencao>>(`${this.apiUrl}/paginado`, { params });
  }

  /**
   * Obtém contagem de status dos tipos de isenção
   * GET /api/admin/tipos-isencao/status
   */
  getStatus(): Observable<StatusCount> {
    return this.http.get<StatusCount>(`${this.apiUrl}/status`);
  }
}
